import type { Component } from 'vue';

import type { TBivarianceHack } from '@/common/types/bivarianceHack';
import { TStorePaths } from '@/common/types/storePaths';
import { TTextAlign } from '@/common/types/style';

import { TFilter } from '../../Filter/types/filter';

import { TPageMetadata } from '@/types/meta';

export type TBaseTableData = Record<string, unknown>;

type TDefaultTableData = Record<string, string | number | boolean | Record<string, unknown>>;

export type TDeepKeyOf<T> = T extends object
  ? {
      [K in Extract<keyof T, string>]: K | `${K}.${TDeepKeyOf<T[K]>}`;
    }[Extract<keyof T, string>]
  : never;

export type TRenderComponentConfig<EntryType extends object = object> = {
  component: Component;
  props?: TBivarianceHack<[rowData: TTableData<EntryType>, rowIndex: number], Record<string, unknown>>;
};

export type TTableColumnConfig<EntryType extends object = object> = {
  field: TDeepKeyOf<EntryType>;
  title: string;
  width?: number;
  sortable?: boolean;
  renderComponent?: TRenderComponentConfig<EntryType>;
  textAlign?: TTextAlign;
  link?: {
    callback: TBivarianceHack<[item: EntryType], string>;
    isBlank?: boolean;
  };
  formatter?: TBivarianceHack<[item: EntryType], string | number>;
};

export type TPaginationConfig = {
  size: number;
};

export type TTableAggregatedProgressBarOptions = {
  title?: string;
  hintText?: string;
};

export type TTableData<T extends object = TDefaultTableData> = {
  id: number;
} & T;

export type TTableTreeData<T extends object = TDefaultTableData> = {
  _key: string;
  level: number;
  children: TTableTreeData<T>[];
  hasChildren: boolean;
  [key: string]: unknown;
};

export type TTableWithOuterFilterConfig<T extends object = TDefaultTableData> = {
  columns: TTableColumnConfig<TTableData<T>>[];
  storePaths: TStorePaths;
  params?: TFilter;
  pagination?: TPaginationConfig;
  isShowNumberColumn?: boolean;
  eventConfigs?: TTableEventConfigs<TTableData<T>>;
  emptyText?: string;
  rowClass?: (row: TTableData<T>) => string | undefined;
};

export type TFootTableItem = {
  text: string | number;
  colSpan?: number;
  class?: string;
};

export type TFootRenderParams<T> = { meta: TPageMetadata | null; items: T[] };

export enum TableEventTypes {
  EDIT = 1,
  DELETE = 2,
  ROW_CLICK = 3,
}

export enum TableEventIconsTypes {
  EDIT = TableEventTypes.EDIT,
  DELETE = TableEventTypes.DELETE,
}

export type TTableEventRowHandler<T = object> = TBivarianceHack<[row: T, resetPagination: () => void]>;

export type TTableEventConfig<T = object> = {
  type: TableEventTypes;
  func: TTableEventRowHandler<T>;
};

export type TTableEventIconsConfig<T = object> = {
  type: Exclude<TableEventTypes, TableEventTypes.ROW_CLICK>;
  func: TTableEventRowHandler<T>;
};

export type TTableEventConfigs<T = object> = Array<TTableEventConfig<T>>;

export type TTableEventIconsConfigs<T = object> = Array<TTableEventIconsConfig<T>>;

export type TTableTreeColumnConfig<T extends object = TDefaultTableData> = TTableColumnConfig<T> & {
  sortable?: boolean;
};

export type TTableTreeConfig<T extends object = TDefaultTableData> = Pick<
  TTableWithOuterFilterConfig<T>,
  'storePaths' | 'params' | 'pagination'
> & {
  columns: TTableTreeColumnConfig<T>[];
};

export enum TableSortTypes {
  ASCEND = 'ascend',
  DESCEND = 'descend',
}

export type TSortParams = {
  sortColumn: string;
  sortOrder: TableSortTypes;
};

export type TAggregatedTableTextAlign = Exclude<TTextAlign, 'center'>;

export type TAggregatedTableMeta<T extends object, TValue = unknown> = {
  class?: string;
  getRowspan?: (row: T) => number | undefined;
  getColspan?: (row: T, value: TValue) => number | undefined;
  fixed?: boolean;
  textAlign?: TAggregatedTableTextAlign | ((row: T) => TAggregatedTableTextAlign | undefined);
};

export type TCachedVirtualRows = Array<{ index: number; start: number; size: number; end: number }>;
